interface DualEngineSolutionsProps {
  onCTAClick: () => void;
}


export default function DualEngineSolutions({ onCTAClick }: DualEngineSolutionsProps) {
  return (
    <section id="dual-engine-solutions" className="py-16 sm:py-28 bg-gradient-to-br from-[#0B1120] via-[#0f1e3a] to-[#0B1120]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* 標題 */}
        <div className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 text-white/70 px-4 py-2 rounded-full text-xs sm:text-sm font-medium mb-5 sm:mb-6">
            <i className="ri-radio-button-line animate-pulse text-yellow-400"></i>
            <span>廣告投放 · 流量引流 · 全鏈轉換</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-5 px-2 leading-tight">
            全網精準廣告投放
            <span className="block mt-2 bg-gradient-to-r from-yellow-300 to-[#D4AF37] bg-clip-text text-transparent">
              ＆ 流量引流引擎
            </span>
          </h2>
          <p className="text-white/60 text-sm sm:text-lg max-w-2xl mx-auto leading-relaxed px-2">
            系統建置完成只是第一步，我們透過 Meta、Google 等多渠道精準廣告投放，
            將真實流量灌入您的專屬漏斗，化為實質營收。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {['Meta Ads', 'Google Ads', 'GTM 追蹤', '再行銷閉環', 'ROAS 優化'].map((tag, i) => (
              <span key={i} className="bg-yellow-400/10 border border-yellow-400/30 text-yellow-300 text-xs px-3 py-1.5 rounded-full font-medium">
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* 三卡片 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-8 mb-10 sm:mb-14">
          {/* 方案 A */}
          <div className="relative bg-gradient-to-br from-[#1a2a4a] to-[#0f1e3a] border border-[#1E3A8A]/50 rounded-2xl sm:rounded-3xl p-6 sm:p-10 overflow-hidden group hover:border-[#1E3A8A] hover:shadow-2xl hover:shadow-[#1E3A8A]/20 transition-all duration-500">
            <div className="absolute top-0 right-0 w-48 sm:w-64 h-48 sm:h-64 bg-[#1E3A8A]/10 rounded-full -translate-y-24 sm:-translate-y-32 translate-x-24 sm:translate-x-32 group-hover:bg-[#1E3A8A]/20 transition-all duration-500"></div>
            <div className="inline-flex items-center gap-2 bg-[#1E3A8A]/30 border border-[#1E3A8A]/50 text-blue-300 px-3 py-1.5 rounded-full text-xs font-semibold mb-5 sm:mb-6">
              <i className="ri-shield-check-line"></i>
              戰略矩陣 Alpha
            </div>
            <div className="flex items-start gap-3 sm:gap-4 mb-5 sm:mb-6">
              <div className="w-11 h-11 sm:w-14 sm:h-14 flex items-center justify-center bg-[#1E3A8A]/40 rounded-xl sm:rounded-2xl flex-shrink-0">
                <i className="ri-filter-3-line text-2xl sm:text-3xl text-blue-300"></i>
              </div>
              <div>
                <h3 className="text-lg sm:text-2xl font-bold text-white mb-1">高摩擦防禦漏斗</h3>
                <p className="text-blue-300 text-xs sm:text-sm font-medium">👉 專為高總價/B2B打造</p>
              </div>
            </div>
            <p className="text-white/70 leading-relaxed mb-6 sm:mb-8 text-sm sm:text-base">
              <strong className="text-white">拒絕無效溝通！</strong>
              透過多層次表單與意願驗證機制，自動擋下無效客源。
              系統確認預算與需求後，<strong className="text-yellow-300">1 秒內</strong>將合格商機 (SQL)
              派發至您的 LINE，極大化業務團隊產值。
            </p>
            <div className="space-y-2.5 sm:space-y-3 mb-6 sm:mb-8">
              {[
                { icon: 'ri-questionnaire-line', text: '多層次意願驗證表單' },
                { icon: 'ri-user-forbid-line', text: '自動過濾無效客源' },
                { icon: 'ri-notification-3-line', text: '1 秒 LINE 即時商機推播' },
                { icon: 'ri-line-chart-line', text: '極大化業務團隊產值' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-[#1E3A8A]/30 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-blue-300 text-xs sm:text-sm`}></i>
                  </div>
                  <span className="text-white/80 text-xs sm:text-sm">{item.text}</span>
                </div>
              ))}
            </div>
            <div className="bg-white/5 rounded-xl p-3 sm:p-4 border border-white/10 mb-5 sm:mb-6">
              <p className="text-white/50 text-xs mb-2 font-medium uppercase tracking-wider">適用產業</p>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {['B2B 製造業', '顧問服務', '高端房產', '企業軟體', '醫美診所'].map((tag, i) => (
                  <span key={i} className="bg-[#1E3A8A]/40 text-blue-200 text-xs px-2.5 py-1 rounded-full border border-[#1E3A8A]/30">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <button
              onClick={onCTAClick}
              className="w-full flex items-center justify-center gap-2 border border-blue-400/50 text-blue-300 hover:bg-blue-400/10 hover:border-blue-400 transition-all duration-300 rounded-xl py-3 text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-right-circle-line"></i>
              了解此方案，立即諮詢
            </button>
          </div>

          {/* 方案 B */}
          <div className="relative bg-gradient-to-br from-[#2a1a0a] to-[#1a0f05] border border-yellow-600/40 rounded-2xl sm:rounded-3xl p-6 sm:p-10 overflow-hidden group hover:border-yellow-500/70 hover:shadow-2xl hover:shadow-yellow-500/20 transition-all duration-500">
            <div className="absolute top-0 right-0 w-48 sm:w-64 h-48 sm:h-64 bg-yellow-500/5 rounded-full -translate-y-24 sm:-translate-y-32 translate-x-24 sm:translate-x-32 group-hover:bg-yellow-500/10 transition-all duration-500"></div>
            <div className="inline-flex items-center gap-2 bg-yellow-500/20 border border-yellow-500/40 text-yellow-300 px-3 py-1.5 rounded-full text-xs font-semibold mb-5 sm:mb-6">
              <i className="ri-flashlight-line"></i>
              戰略矩陣 Beta
            </div>
            <div className="flex items-start gap-3 sm:gap-4 mb-5 sm:mb-6">
              <div className="w-11 h-11 sm:w-14 sm:h-14 flex items-center justify-center bg-yellow-500/20 rounded-xl sm:rounded-2xl flex-shrink-0">
                <i className="ri-speed-up-line text-2xl sm:text-3xl text-yellow-300"></i>
              </div>
              <div>
                <h3 className="text-lg sm:text-2xl font-bold text-white mb-1">無感極速轉換漏斗</h3>
                <p className="text-yellow-300 text-xs sm:text-sm font-medium">👉 專為電商/實體店家打造</p>
              </div>
            </div>
            <p className="text-white/70 leading-relaxed mb-6 sm:mb-8 text-sm sm:text-base">
              <strong className="text-white">打破結帳阻力！</strong>
              透過高爆發力的短影音引流，結合極低摩擦力的著陸頁設計與 API 自動化再行銷。
              讓消費者從看到廣告到下單/預約，享受絲滑體驗，極大化您的
              <strong className="text-yellow-300">廣告投報率 (ROAS)</strong>。
            </p>
            <div className="space-y-2.5 sm:space-y-3 mb-6 sm:mb-8">
              {[
                { icon: 'ri-vidicon-2-line', text: '高爆發力短影音引流' },
                { icon: 'ri-layout-line', text: '極低摩擦力著陸頁設計' },
                { icon: 'ri-refresh-line', text: 'API 自動化再行銷系統' },
                { icon: 'ri-line-chart-line', text: '廣告投報率 (ROAS) 極大化' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-yellow-500/20 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-yellow-300 text-xs sm:text-sm`}></i>
                  </div>
                  <span className="text-white/80 text-xs sm:text-sm">{item.text}</span>
                </div>
              ))}
            </div>
            <div className="bg-white/5 rounded-xl p-3 sm:p-4 border border-white/10 mb-5 sm:mb-6">
              <p className="text-white/50 text-xs mb-2 font-medium uppercase tracking-wider">適用產業</p>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {['電商零售', '餐飲美食', '健身美容', '在地服務', '線上課程'].map((tag, i) => (
                  <span key={i} className="bg-yellow-500/20 text-yellow-200 text-xs px-2.5 py-1 rounded-full border border-yellow-500/30">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <button
              onClick={onCTAClick}
              className="w-full flex items-center justify-center gap-2 border border-yellow-400/50 text-yellow-300 hover:bg-yellow-400/10 hover:border-yellow-400 transition-all duration-300 rounded-xl py-3 text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-right-circle-line"></i>
              了解此方案，立即諮詢
            </button>
          </div>

          {/* 方案 C */}
          <div className="relative bg-gradient-to-br from-[#1a1a2a] to-[#0a0a15] border border-purple-600/40 rounded-2xl sm:rounded-3xl p-6 sm:p-10 overflow-hidden group hover:border-purple-500/70 hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-500">
            <div className="absolute top-0 right-0 w-48 sm:w-64 h-48 sm:h-64 bg-purple-500/5 rounded-full -translate-y-24 sm:-translate-y-32 translate-x-24 sm:translate-x-32 group-hover:bg-purple-500/10 transition-all duration-500"></div>
            <div className="inline-flex items-center gap-2 bg-purple-500/20 border border-purple-500/40 text-purple-300 px-3 py-1.5 rounded-full text-xs font-semibold mb-5 sm:mb-6">
              <i className="ri-global-line"></i>
              戰略矩陣 Gamma
            </div>
            <div className="flex items-start gap-3 sm:gap-4 mb-5 sm:mb-6">
              <div className="w-11 h-11 sm:w-14 sm:h-14 flex items-center justify-center bg-purple-500/20 rounded-xl sm:rounded-2xl flex-shrink-0">
                <i className="ri-radar-line text-2xl sm:text-3xl text-purple-300"></i>
              </div>
              <div>
                <h3 className="text-lg sm:text-2xl font-bold text-white mb-1">全網精準打擊矩陣</h3>
                <p className="text-purple-300 text-xs sm:text-sm font-medium">👉 數據驅動決策</p>
              </div>
            </div>
            <p className="text-white/70 leading-relaxed mb-6 sm:mb-8 text-sm sm:text-base">
              <strong className="text-white">每一分廣告預算的流向，都必須對應真實轉化。</strong>
              透過 Meta、Google、TikTok 與 LINE 頂尖演算法，主動捕捉具備真實購買力的高意願受眾。
              讓您的品牌在對的時間、對的平台，精準觸及<strong className="text-purple-300">對的人</strong>。
            </p>
            <div className="space-y-2.5 sm:space-y-3 mb-6 sm:mb-8">
              {[
                { icon: 'ri-facebook-circle-line', text: 'Meta 生態系精準投放' },
                { icon: 'ri-google-line', text: 'Google Ads 搜尋意圖捕捉' },
                { icon: 'ri-tiktok-line', text: 'TikTok 短影音矩陣突圍' },
                { icon: 'ri-line-line', text: 'LINE 商業生態圈閉環' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-purple-500/20 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-purple-300 text-xs sm:text-sm`}></i>
                  </div>
                  <span className="text-white/80 text-xs sm:text-sm">{item.text}</span>
                </div>
              ))}
            </div>
            <div className="bg-white/5 rounded-xl p-3 sm:p-4 border border-white/10 mb-5 sm:mb-6">
              <p className="text-white/50 text-xs mb-2 font-medium uppercase tracking-wider">適用場景</p>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {['全通路佈局', '品牌曝光', '精準再行銷', '數據追蹤', '成本優化'].map((tag, i) => (
                  <span key={i} className="bg-purple-500/20 text-purple-200 text-xs px-2.5 py-1 rounded-full border border-purple-500/30">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <button
              onClick={onCTAClick}
              className="w-full flex items-center justify-center gap-2 border border-purple-400/50 text-purple-300 hover:bg-purple-400/10 hover:border-purple-400 transition-all duration-300 rounded-xl py-3 text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-right-circle-line"></i>
              了解此方案，立即諮詢
            </button>
          </div>
        </div>

        {/* 底部 CTA */}
        <div className="text-center">
          <p className="text-white/50 text-xs sm:text-sm mb-4 sm:mb-5">不確定哪種方案適合您？讓我們的架構師為您免費診斷</p>
          <button
            onClick={onCTAClick}
            className="bg-yellow-400 text-[#0B1120] px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-sm sm:text-base font-bold hover:bg-yellow-300 transition-all transform hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center gap-2 shadow-lg shadow-yellow-400/20"
          >
            <i className="ri-customer-service-2-line"></i>
            免費預約漏斗診斷
          </button>
        </div>
      </div>
    </section>
  );
}
